// See messages/README.md for how to add strings.
// The /create studio's Sound and Video sheets (components/studio/SoundOptions,
// VideoOptions) and its upload and record views (components/modals/
// UploadSongView, MicrophoneRecordView). Effect names come from the API and
// stay as they are.
export const studioOptions = {
  en: {
    // Sound sheet
    "studioOptions.pitch.title": "Pitch",
    "studioOptions.pitch.hint":
      "In semitones. Going from a male to a female voice? Try +12.",
    // aria-label: "Lower pitch for Drake"
    "studioOptions.pitch.label": "pitch for {name}",
    "studioOptions.pitch.maleToFemale": "Male → female",
    "studioOptions.pitch.femaleToMale": "Female → male",
    "studioOptions.pitch.original": "Original",
    "studioOptions.stepper.lower": "Lower {label}",
    "studioOptions.stepper.raise": "Raise {label}",
    "studioOptions.effect.title": "Effect",
    "studioOptions.effect.hint": "One at a time. Tap again to remove it.",
    "studioOptions.effect.none": "None",
    "studioOptions.effect.onVocals": "On the vocals",
    "studioOptions.effect.onSong": "On the whole song",
    "studioOptions.autotune.title": "Autotune",
    "studioOptions.autotune.switch": "Snap the vocals to key",
    "studioOptions.autotune.hint":
      "From a light polish to the obvious robotic sound.",
    "studioOptions.autotune.light": "Light",
    "studioOptions.autotune.medium": "Medium",
    "studioOptions.autotune.strong": "Strong",
    "studioOptions.mix.title": "Mix",
    "studioOptions.mix.vocalVolume": "Vocal volume",
    "studioOptions.mix.vocalVolumeHint":
      "Louder or quieter than the music, in dB.",
    // aria-label: "Lower vocal volume"
    "studioOptions.mix.vocalVolumeLabel": "vocal volume",
    "studioOptions.mix.backing": "Split out backing vocals",
    "studioOptions.mix.backingHint":
      "Cleaner result when the song has harmonies or ad-libs.",
    "studioOptions.mix.vocalsOnly": "My audio is just vocals",
    "studioOptions.mix.vocalsOnlyHint":
      "Skips separating the vocals from the music.",
    "studioOptions.name.title": "Cover name",
    "studioOptions.name.placeholder": "Optional",

    // The Sound card's one-line summary
    "studioOptions.summary.pitch": "Pitch {values}",
    "studioOptions.summary.autotune": "Autotune",
    "studioOptions.summary.vocals": "Vocals {value}",
    "studioOptions.summary.backing": "Backing vocals split",
    "studioOptions.summary.vocalsOnly": "Vocals only input",
    "studioOptions.summary.default": "Original pitch, no effects",

    // Video sheet (Marble Run)
    "studioOptions.video.previewAria": "Preview of the Marble Run",
    "studioOptions.video.headline": "Your voices race. Whoever leads, sings.",
    "studioOptions.video.body":
      "Lead changes become voice changes, with the lyrics on screen. Ready to post on TikTok and Reels.",
    "studioOptions.video.crown": "The crown shows who's singing",
    "studioOptions.video.pickLook": "Pick a look",
    "studioOptions.video.customise": "Customise",
    "studioOptions.video.surprise": "Surprise me",
    "studioOptions.video.marbles": "Marbles and track",
    "studioOptions.video.sky": "Sky",
    "studioOptions.video.font": "Lyrics font",
    "studioOptions.video.stars": "Stars in the sky",
    "studioOptions.theme.midnight": "Midnight",
    "studioOptions.theme.neon": "Neon",
    "studioOptions.theme.ocean": "Ocean",
    "studioOptions.theme.royal": "Royal",
    "studioOptions.theme.lava": "Lava",
    "studioOptions.theme.gold": "Gold",

    // Upload view
    "studioOptions.upload.empty":
      "This file is empty. Please choose the song file again.",
    "studioOptions.upload.tooBig":
      "File size exceeds 75MB! Please upload a smaller file.",
    "studioOptions.upload.tooLong":
      "Audio duration exceeds 6 minutes! Please upload a shorter file.",
    "studioOptions.upload.invalid": "Please upload a valid audio file",
    "studioOptions.upload.unexpected": "An unexpected error occurred.",
    "studioOptions.upload.uploading": "Uploading...",
    "studioOptions.upload.coverAlt": "cover",
    "studioOptions.upload.dropIt": "Drop it",
    "studioOptions.upload.dropHere": "Drop your audio here",
    "studioOptions.upload.browse": "Browse files",
    "studioOptions.upload.formats": "MP3, WAV or M4A · up to 6 minutes",
    "studioOptions.upload.dropOrBrowse":
      "Drop an audio file or click to browse",
    "studioOptions.upload.limits": "Max 75MB, 6 mins • MP3, WAV, M4A",

    // Record view
    "studioOptions.mic.denied":
      "Allow microphone access in your browser to record.",
    "studioOptions.mic.notFound": "Couldn't find a microphone to record with.",
    "studioOptions.mic.prepareFailed": "Failed to prepare upload location.",
    "studioOptions.mic.fileFailed": "Failed to upload file.",
    "studioOptions.mic.uploadFailed": "Microphone upload failed.",
    "studioOptions.mic.previewFailed": "Could not play audio preview.",
    "studioOptions.mic.start": "Start recording",
    "studioOptions.mic.stop": "Stop recording",
    "studioOptions.mic.tapRecord": "Tap to record",
    "studioOptions.mic.tapStop": "Tap to stop",
    "studioOptions.mic.clickRecord": "Click to record",
    "studioOptions.mic.processing": "Processing...",
    "studioOptions.mic.playPreview": "Play preview",
    "studioOptions.mic.pausePreview": "Pause preview",
    "studioOptions.mic.yourRecording": "Your recording",
    "studioOptions.mic.use": "Use this recording",
    "studioOptions.mic.again": "Record again",
    "studioOptions.mic.continue": "Continue",
  },
  es: {
    "studioOptions.pitch.title": "Tono",
    "studioOptions.pitch.hint":
      "En semitonos. ¿De voz masculina a femenina? Prueba +12.",
    "studioOptions.pitch.label": "el tono de {name}",
    "studioOptions.pitch.maleToFemale": "Masculina → femenina",
    "studioOptions.pitch.femaleToMale": "Femenina → masculina",
    "studioOptions.pitch.original": "Original",
    "studioOptions.stepper.lower": "Bajar {label}",
    "studioOptions.stepper.raise": "Subir {label}",
    "studioOptions.effect.title": "Efecto",
    "studioOptions.effect.hint": "Uno a la vez. Tócalo de nuevo para quitarlo.",
    "studioOptions.effect.none": "Ninguno",
    "studioOptions.effect.onVocals": "En la voz",
    "studioOptions.effect.onSong": "En toda la canción",
    "studioOptions.autotune.title": "Autotune",
    "studioOptions.autotune.switch": "Afinar la voz al tono",
    "studioOptions.autotune.hint":
      "Desde un retoque sutil hasta el clásico sonido robótico.",
    "studioOptions.autotune.light": "Suave",
    "studioOptions.autotune.medium": "Medio",
    "studioOptions.autotune.strong": "Fuerte",
    "studioOptions.mix.title": "Mezcla",
    "studioOptions.mix.vocalVolume": "Volumen de la voz",
    "studioOptions.mix.vocalVolumeHint":
      "Más alto o más bajo que la música, en dB.",
    "studioOptions.mix.vocalVolumeLabel": "el volumen de la voz",
    "studioOptions.mix.backing": "Separar los coros",
    "studioOptions.mix.backingHint":
      "Resultado más limpio si la canción tiene armonías o ad-libs.",
    "studioOptions.mix.vocalsOnly": "Mi audio es solo voz",
    "studioOptions.mix.vocalsOnlyHint": "No separa la voz de la música.",
    "studioOptions.name.title": "Nombre del cover",
    "studioOptions.name.placeholder": "Opcional",

    "studioOptions.summary.pitch": "Tono {values}",
    "studioOptions.summary.autotune": "Autotune",
    "studioOptions.summary.vocals": "Voz {value}",
    "studioOptions.summary.backing": "Coros separados",
    "studioOptions.summary.vocalsOnly": "Audio solo de voz",
    "studioOptions.summary.default": "Tono original, sin efectos",

    "studioOptions.video.previewAria": "Vista previa de la Marble Run",
    "studioOptions.video.headline":
      "Tus voces compiten. La que va adelante, canta.",
    "studioOptions.video.body":
      "Cada cambio de líder es un cambio de voz, con la letra en pantalla. Listo para publicar en TikTok y Reels.",
    "studioOptions.video.crown": "La corona muestra quién canta",
    "studioOptions.video.pickLook": "Elige un estilo",
    "studioOptions.video.customise": "Personalizar",
    "studioOptions.video.surprise": "Sorpréndeme",
    "studioOptions.video.marbles": "Canicas y pista",
    "studioOptions.video.sky": "Cielo",
    "studioOptions.video.font": "Fuente de la letra",
    "studioOptions.video.stars": "Estrellas en el cielo",
    "studioOptions.theme.midnight": "Medianoche",
    "studioOptions.theme.neon": "Neón",
    "studioOptions.theme.ocean": "Océano",
    "studioOptions.theme.royal": "Realeza",
    "studioOptions.theme.lava": "Lava",
    "studioOptions.theme.gold": "Oro",

    "studioOptions.upload.empty":
      "Este archivo está vacío. Vuelve a elegir el archivo de la canción.",
    "studioOptions.upload.tooBig":
      "¡El archivo supera los 75 MB! Sube un archivo más pequeño.",
    "studioOptions.upload.tooLong":
      "¡El audio dura más de 6 minutos! Sube un archivo más corto.",
    "studioOptions.upload.invalid": "Sube un archivo de audio válido",
    "studioOptions.upload.unexpected": "Ocurrió un error inesperado.",
    "studioOptions.upload.uploading": "Subiendo...",
    "studioOptions.upload.coverAlt": "portada",
    "studioOptions.upload.dropIt": "Suéltalo",
    "studioOptions.upload.dropHere": "Suelta tu audio aquí",
    "studioOptions.upload.browse": "Buscar archivos",
    "studioOptions.upload.formats": "MP3, WAV o M4A · hasta 6 minutos",
    "studioOptions.upload.dropOrBrowse":
      "Suelta un archivo de audio o haz clic para buscarlo",
    "studioOptions.upload.limits": "Máx. 75 MB, 6 min • MP3, WAV, M4A",

    "studioOptions.mic.denied":
      "Permite el acceso al micrófono en tu navegador para grabar.",
    "studioOptions.mic.notFound": "No encontramos un micrófono para grabar.",
    "studioOptions.mic.prepareFailed": "No se pudo preparar la subida.",
    "studioOptions.mic.fileFailed": "No se pudo subir el archivo.",
    "studioOptions.mic.uploadFailed": "No se pudo subir la grabación.",
    "studioOptions.mic.previewFailed": "No se pudo reproducir la vista previa.",
    "studioOptions.mic.start": "Empezar a grabar",
    "studioOptions.mic.stop": "Detener la grabación",
    "studioOptions.mic.tapRecord": "Toca para grabar",
    "studioOptions.mic.tapStop": "Toca para detener",
    "studioOptions.mic.clickRecord": "Haz clic para grabar",
    "studioOptions.mic.processing": "Procesando...",
    "studioOptions.mic.playPreview": "Reproducir vista previa",
    "studioOptions.mic.pausePreview": "Pausar vista previa",
    "studioOptions.mic.yourRecording": "Tu grabación",
    "studioOptions.mic.use": "Usar esta grabación",
    "studioOptions.mic.again": "Grabar de nuevo",
    "studioOptions.mic.continue": "Continuar",
  },
  pt: {
    "studioOptions.pitch.title": "Tom",
    "studioOptions.pitch.hint":
      "Em semitons. De voz masculina para feminina? Tente +12.",
    "studioOptions.pitch.label": "o tom de {name}",
    "studioOptions.pitch.maleToFemale": "Masculina → feminina",
    "studioOptions.pitch.femaleToMale": "Feminina → masculina",
    "studioOptions.pitch.original": "Original",
    "studioOptions.stepper.lower": "Diminuir {label}",
    "studioOptions.stepper.raise": "Aumentar {label}",
    "studioOptions.effect.title": "Efeito",
    "studioOptions.effect.hint": "Um por vez. Toque de novo para remover.",
    "studioOptions.effect.none": "Nenhum",
    "studioOptions.effect.onVocals": "Na voz",
    "studioOptions.effect.onSong": "Na música inteira",
    "studioOptions.autotune.title": "Autotune",
    "studioOptions.autotune.switch": "Afinar a voz no tom",
    "studioOptions.autotune.hint":
      "De um leve retoque ao som robótico bem marcado.",
    "studioOptions.autotune.light": "Leve",
    "studioOptions.autotune.medium": "Médio",
    "studioOptions.autotune.strong": "Forte",
    "studioOptions.mix.title": "Mixagem",
    "studioOptions.mix.vocalVolume": "Volume da voz",
    "studioOptions.mix.vocalVolumeHint":
      "Mais alto ou mais baixo que a música, em dB.",
    "studioOptions.mix.vocalVolumeLabel": "o volume da voz",
    "studioOptions.mix.backing": "Separar os vocais de apoio",
    "studioOptions.mix.backingHint":
      "Resultado mais limpo quando a música tem harmonias ou ad-libs.",
    "studioOptions.mix.vocalsOnly": "Meu áudio é só voz",
    "studioOptions.mix.vocalsOnlyHint": "Pula a separação da voz e da música.",
    "studioOptions.name.title": "Nome do cover",
    "studioOptions.name.placeholder": "Opcional",

    "studioOptions.summary.pitch": "Tom {values}",
    "studioOptions.summary.autotune": "Autotune",
    "studioOptions.summary.vocals": "Voz {value}",
    "studioOptions.summary.backing": "Vocais de apoio separados",
    "studioOptions.summary.vocalsOnly": "Áudio só de voz",
    "studioOptions.summary.default": "Tom original, sem efeitos",

    "studioOptions.video.previewAria": "Prévia da Marble Run",
    "studioOptions.video.headline":
      "Suas vozes apostam corrida. Quem lidera, canta.",
    "studioOptions.video.body":
      "Cada troca de liderança vira uma troca de voz, com a letra na tela. Pronto para postar no TikTok e no Reels.",
    "studioOptions.video.crown": "A coroa mostra quem está cantando",
    "studioOptions.video.pickLook": "Escolha um visual",
    "studioOptions.video.customise": "Personalizar",
    "studioOptions.video.surprise": "Me surpreenda",
    "studioOptions.video.marbles": "Bolinhas e pista",
    "studioOptions.video.sky": "Céu",
    "studioOptions.video.font": "Fonte da letra",
    "studioOptions.video.stars": "Estrelas no céu",
    "studioOptions.theme.midnight": "Meia-noite",
    "studioOptions.theme.neon": "Neon",
    "studioOptions.theme.ocean": "Oceano",
    "studioOptions.theme.royal": "Realeza",
    "studioOptions.theme.lava": "Lava",
    "studioOptions.theme.gold": "Ouro",

    "studioOptions.upload.empty":
      "Este arquivo está vazio. Escolha o arquivo da música de novo.",
    "studioOptions.upload.tooBig":
      "O arquivo passa de 75 MB! Envie um arquivo menor.",
    "studioOptions.upload.tooLong":
      "O áudio passa de 6 minutos! Envie um arquivo mais curto.",
    "studioOptions.upload.invalid": "Envie um arquivo de áudio válido",
    "studioOptions.upload.unexpected": "Ocorreu um erro inesperado.",
    "studioOptions.upload.uploading": "Enviando...",
    "studioOptions.upload.coverAlt": "capa",
    "studioOptions.upload.dropIt": "Solte aqui",
    "studioOptions.upload.dropHere": "Solte seu áudio aqui",
    "studioOptions.upload.browse": "Procurar arquivos",
    "studioOptions.upload.formats": "MP3, WAV ou M4A · até 6 minutos",
    "studioOptions.upload.dropOrBrowse":
      "Solte um arquivo de áudio ou clique para procurar",
    "studioOptions.upload.limits": "Máx. 75 MB, 6 min • MP3, WAV, M4A",

    "studioOptions.mic.denied":
      "Permita o acesso ao microfone no navegador para gravar.",
    "studioOptions.mic.notFound": "Não encontramos um microfone para gravar.",
    "studioOptions.mic.prepareFailed": "Não foi possível preparar o envio.",
    "studioOptions.mic.fileFailed": "Não foi possível enviar o arquivo.",
    "studioOptions.mic.uploadFailed": "Não foi possível enviar a gravação.",
    "studioOptions.mic.previewFailed": "Não foi possível tocar a prévia.",
    "studioOptions.mic.start": "Começar a gravar",
    "studioOptions.mic.stop": "Parar a gravação",
    "studioOptions.mic.tapRecord": "Toque para gravar",
    "studioOptions.mic.tapStop": "Toque para parar",
    "studioOptions.mic.clickRecord": "Clique para gravar",
    "studioOptions.mic.processing": "Processando...",
    "studioOptions.mic.playPreview": "Tocar prévia",
    "studioOptions.mic.pausePreview": "Pausar prévia",
    "studioOptions.mic.yourRecording": "Sua gravação",
    "studioOptions.mic.use": "Usar esta gravação",
    "studioOptions.mic.again": "Gravar de novo",
    "studioOptions.mic.continue": "Continuar",
  },
};
